import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import { Insight, InsightCreate, DimensionFilter } from '../../types';

export default function InsightsManager() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [companies, setCompanies] = useState<string[]>([]);
  const [dimensionFields, setDimensionFields] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [filters, setFilters] = useState({
    company: '',
    dimension_field: '',
    dimension_value: '',
    reporting_period: '',
    insight_type: '',
  });

  useEffect(() => {
    loadInsights();
  }, [filters, showArchived]);

  useEffect(() => {
    loadCompanies();
    loadDimensionFields();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await apiGet('/api/insights/companies');
      setCompanies(await response.json());
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadDimensionFields = async () => {
    try {
      const response = await apiGet('/api/insights/dimension-fields');
      setDimensionFields(await response.json());
    } catch (error) {
      console.error('Failed to load dimension fields:', error);
    }
  };

  const loadInsights = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.company) params.append('company', filters.company);
      if (filters.dimension_field) params.append('dimension_field', filters.dimension_field);
      if (filters.dimension_value) params.append('dimension_value', filters.dimension_value);
      if (filters.reporting_period) params.append('reporting_period', filters.reporting_period);
      if (filters.insight_type) params.append('insight_type', filters.insight_type);
      params.append('status', showArchived ? 'archived' : 'active');

      const response = await apiGet(`/api/insights?${params.toString()}`);
      const data = await response.json();
      setInsights(data);
    } catch (error) {
      console.error('Failed to load insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedInsight(null);
    setIsEditing(true);
  };

  const handleEdit = (insight: Insight) => {
    setSelectedInsight(insight);
    setIsEditing(true);
  };

  const handleArchive = async (insightId: string) => {
    if (!window.confirm('Archive this insight?')) return;
    try {
      await apiDelete(`/api/insights/${insightId}`);
      loadInsights();
    } catch (error) {
      console.error('Failed to archive insight:', error);
      alert('Failed to archive insight');
    }
  };

  const handleUnarchive = async (insightId: string) => {
    try {
      await apiPut(`/api/insights/${insightId}/unarchive`, {});
      loadInsights();
    } catch (error) {
      console.error('Failed to unarchive insight:', error);
      alert('Failed to unarchive insight');
    }
  };

  const handleExportCsv = async () => {
    const params = new URLSearchParams();
    if (filters.company) params.append('company', filters.company);
    if (filters.dimension_field) params.append('dimension_field', filters.dimension_field);
    if (filters.dimension_value) params.append('dimension_value', filters.dimension_value);
    if (filters.reporting_period) params.append('reporting_period', filters.reporting_period);
    if (filters.insight_type) params.append('insight_type', filters.insight_type);
    params.append('status', showArchived ? 'archived' : 'active');

    try {
      const response = await apiGet(`/api/insights/export/csv?${params.toString()}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `insights_${filters.company || 'all'}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export:', error);
      alert('Failed to export insights');
    }
  };

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/insights/import/csv', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await response.json();
      alert(`Imported ${data.imported} insights.${data.errors?.length ? '\n\nWarnings:\n' + data.errors.join('\n') : ''}`);
      loadInsights();
      loadCompanies();
      loadDimensionFields();
    } catch (error) {
      console.error('Failed to import:', error);
      alert('Failed to import insights');
    }

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const inputStyle = {
    flex: 1,
    minWidth: 120,
    padding: '6px 10px',
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    background: theme.background,
    color: theme.text,
    fontSize: 13,
  };

  const smallBtnStyle = {
    padding: '6px 12px',
    background: theme.background,
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    cursor: 'pointer' as const,
    fontSize: 12,
    fontWeight: 500 as const,
    color: theme.text,
  };

  return (
    <div style={{ background: theme.background, minHeight: '100vh', padding: '20px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Insights Manager</h1>
            <p style={{ color: theme.textSecondary, fontSize: 13 }}>
              {insights.length} {showArchived ? 'archived' : 'active'} insight{insights.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={handleExportCsv} style={smallBtnStyle} title="Export filtered insights as CSV">
              ↓ Export CSV
            </button>
            <button onClick={() => fileInputRef.current?.click()} style={smallBtnStyle} title="Import insights from CSV">
              ↑ Import CSV
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCsv}
              style={{ display: 'none' }}
            />
            {!showArchived && (
              <button
                onClick={handleCreate}
                style={{
                  padding: '8px 16px',
                  background: theme.buttonBackground,
                  color: theme.buttonText,
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                + New Insight
              </button>
            )}
          </div>
        </div>

        {/* Filters + Archive Toggle */}
        <div style={{
          background: theme.cardBackground,
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <input
            type="text"
            placeholder="Company..."
            list="company-filter-list"
            value={filters.company}
            onChange={(e) => setFilters({ ...filters, company: e.target.value })}
            style={inputStyle}
          />
          <datalist id="company-filter-list">
            {companies.map(c => <option key={c} value={c} />)}
          </datalist>
          <input
            type="text"
            placeholder="Dimension field..."
            list="dim-field-filter-list"
            value={filters.dimension_field}
            onChange={(e) => setFilters({ ...filters, dimension_field: e.target.value })}
            style={inputStyle}
          />
          <datalist id="dim-field-filter-list">
            {dimensionFields.map(f => <option key={f} value={f} />)}
          </datalist>
          <input
            type="text"
            placeholder="Dimension value..."
            value={filters.dimension_value}
            onChange={(e) => setFilters({ ...filters, dimension_value: e.target.value })}
            style={inputStyle}
          />
          <input
            type="date"
            placeholder="Reporting period..."
            value={filters.reporting_period}
            onChange={(e) => setFilters({ ...filters, reporting_period: e.target.value })}
            style={inputStyle}
          />
          <select
            value={filters.insight_type}
            onChange={(e) => setFilters({ ...filters, insight_type: e.target.value })}
            style={{ ...inputStyle, minWidth: 90 }}
          >
            <option value="">All Types</option>
            <option value="Manual">Manual</option>
            <option value="Generated">Generated</option>
          </select>

          {/* Archive toggle */}
          <div style={{ borderLeft: `1px solid ${theme.border}`, paddingLeft: 8, marginLeft: 4 }}>
            <button
              onClick={() => setShowArchived(!showArchived)}
              style={{
                ...smallBtnStyle,
                background: showArchived ? (theme.dangerButton || '#ef4444') + '18' : theme.background,
                color: showArchived ? (theme.dangerButton || '#ef4444') : theme.textSecondary,
                borderColor: showArchived ? (theme.dangerButton || '#ef4444') + '60' : theme.border,
              }}
            >
              {showArchived ? '✕ Archived' : '🗄 Archived'}
            </button>
          </div>
        </div>

        {/* Insights List — compact table-style */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.textSecondary }}>
            Loading insights...
          </div>
        ) : insights.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 40,
            background: theme.cardBackground,
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>💡</div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
              {showArchived ? 'No Archived Insights' : 'No Insights Yet'}
            </h3>
            <p style={{ color: theme.textSecondary, fontSize: 13 }}>
              {showArchived ? 'No insights have been archived' : 'Create your first insight to get started'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {insights.map((insight) => (
              <CompactInsightRow
                key={insight.insight_id}
                insight={insight}
                theme={theme}
                isArchived={showArchived}
                onEdit={handleEdit}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {isEditing && (
        <InsightEditor
          insight={selectedInsight}
          theme={theme}
          companies={companies}
          dimensionFields={dimensionFields}
          onClose={() => setIsEditing(false)}
          onSave={() => {
            setIsEditing(false);
            loadInsights();
            loadCompanies();
            loadDimensionFields();
          }}
        />
      )}
    </div>
  );
}


// ─── Compact Insight Row ────────────────────────────────────────────
function CompactInsightRow({ insight, theme, isArchived, onEdit, onArchive, onUnarchive }: any) {
  const [expanded, setExpanded] = useState(false);

  // Format period as MMM-yy
  let periodLabel = insight.reporting_period;
  try {
    const d = new Date(insight.reporting_period + 'T00:00:00');
    periodLabel = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  } catch {}

  const tagBadgeStyle = {
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500 as const,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div
      style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        padding: '8px 12px',
        opacity: isArchived ? 0.7 : 1,
      }}
    >
      {/* Main row — single line */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Period */}
        <span style={{
          ...tagBadgeStyle,
          background: theme.buttonBackground + '18',
          color: theme.buttonBackground,
          minWidth: 55,
          textAlign: 'center' as const,
        }}>
          {periodLabel}
        </span>

        {/* Type badge */}
        <span style={{
          ...tagBadgeStyle,
          background: insight.insight_type === 'Generated' ? (theme.successLight || '#10b98120') : (theme.infoLight || '#3b82f620'),
          color: insight.insight_type === 'Generated' ? (theme.success || '#10b981') : (theme.info || '#3b82f6'),
        }}>
          {insight.insight_type === 'Generated' ? 'Gen' : 'Man'}
        </span>

        {/* Company badge */}
        {insight.company && (
          <span style={{
            ...tagBadgeStyle,
            background: theme.background,
            border: `1px solid ${theme.border}`,
            color: theme.text,
          }}>
            {insight.company}
          </span>
        )}

        {/* Dimension badges */}
        {insight.dimensions?.map((d: DimensionFilter, i: number) => (
          <span key={i} style={{
            ...tagBadgeStyle,
            background: theme.buttonBackground + '12',
            border: `1px solid ${theme.buttonBackground}30`,
            color: theme.text,
          }}>
            {d.field}: {d.value}
          </span>
        ))}

        {/* Headline — fills remaining space */}
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {insight.headline}
        </span>

        {/* Expand indicator */}
        <span style={{ color: theme.textSecondary, fontSize: 11, flexShrink: 0 }}>
          {(insight.why || insight.action) ? (expanded ? '▲' : '▼') : ''}
        </span>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          {isArchived ? (
            <button
              onClick={() => onUnarchive(insight.insight_id)}
              style={{
                padding: '3px 8px',
                background: (theme.success || '#10b981') + '18',
                border: `1px solid ${(theme.success || '#10b981')}40`,
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
                color: theme.success || '#10b981',
              }}
            >
              Restore
            </button>
          ) : (
            <>
              <button
                onClick={() => onEdit(insight)}
                style={{
                  padding: '3px 8px',
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 11,
                  color: theme.text,
                }}
              >
                Edit
              </button>
              <button
                onClick={() => onArchive(insight.insight_id)}
                style={{
                  padding: '3px 8px',
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 11,
                  color: theme.dangerButton || '#ef4444',
                }}
              >
                Archive
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (insight.why || insight.action || (insight.tags && insight.tags.length > 0)) && (
        <div style={{ marginTop: 6, paddingLeft: 65, fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>
          {insight.why && <p style={{ marginBottom: 2 }}><strong>Why:</strong> {insight.why}</p>}
          {insight.action && <p style={{ marginBottom: 2 }}><strong>Action:</strong> {insight.action}</p>}
          {insight.tags && insight.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              {insight.tags.map((tag: string) => (
                <span key={tag} style={{
                  padding: '1px 6px',
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 3,
                  fontSize: 10,
                  color: theme.textSecondary,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Insight Editor Component (Modal) ───────────────────────────────
function InsightEditor({ insight, theme, companies, dimensionFields, onClose, onSave }: any) {
  const [formData, setFormData] = useState<InsightCreate>(
    insight ? {
      company: insight.company || '',
      reporting_period: insight.reporting_period,
      insight_type: insight.insight_type,
      dimensions: insight.dimensions || [],
      headline: insight.headline,
      why: insight.why || '',
      action: insight.action || '',
      tags: insight.tags || [],
    } : {
      company: '',
      reporting_period: new Date().toISOString().split('T')[0],
      insight_type: 'Manual',
      dimensions: [],
      headline: '',
      why: '',
      action: '',
      tags: [],
    }
  );

  const [tagInput, setTagInput] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Clean up empty optional fields
      const payload = {
        ...formData,
        why: formData.why || undefined,
        action: formData.action || undefined,
        company: formData.company || undefined,
      };

      if (insight) {
        await apiPut(`/api/insights/${insight.insight_id}`, payload);
      } else {
        await apiPost('/api/insights/', payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save insight:', error);
      alert('Failed to save insight');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: (formData.tags || []).filter(t => t !== tag)
    });
  };

  const addDimension = () => {
    setFormData({
      ...formData,
      dimensions: [...formData.dimensions, { field: '', value: '' }],
    });
  };

  const updateDimension = (index: number, key: 'field' | 'value', val: string) => {
    const dims = [...formData.dimensions];
    dims[index] = { ...dims[index], [key]: val };
    setFormData({ ...formData, dimensions: dims });
  };

  const removeDimension = (index: number) => {
    setFormData({
      ...formData,
      dimensions: formData.dimensions.filter((_, i) => i !== index),
    });
  };

  const fieldInputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${theme.border}`,
    borderRadius: 6,
    background: theme.background,
    color: theme.text,
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20,
    }}>
      <div style={{
        background: theme.cardBackground,
        borderRadius: 12,
        padding: 24,
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20, color: theme.text }}>
          {insight ? 'Edit Insight' : 'New Insight'}
        </h2>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
          {/* Company */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Company
            </label>
            <input
              type="text"
              list="company-list"
              value={formData.company || ''}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Type or select a company..."
              style={fieldInputStyle}
            />
            <datalist id="company-list">
              {companies.map((c: string) => <option key={c} value={c} />)}
            </datalist>
          </div>

          {/* Dimensions */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Dimensions
              <span style={{ fontWeight: 400, color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>
                Leave empty for company-wide insight
              </span>
            </label>
            {formData.dimensions.map((dim, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  list="dim-fields-list"
                  placeholder="Field (e.g. ProductGroup)"
                  value={dim.field}
                  onChange={(e) => updateDimension(i, 'field', e.target.value)}
                  required
                  style={{ ...fieldInputStyle, flex: 1 }}
                />
                <input
                  type="text"
                  placeholder="Value (e.g. Fibre 100Mbps)"
                  value={dim.value}
                  onChange={(e) => updateDimension(i, 'value', e.target.value)}
                  required
                  style={{ ...fieldInputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => removeDimension(i)}
                  style={{
                    padding: '8px 12px',
                    background: theme.background,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    color: theme.dangerButton || '#ef4444',
                    fontSize: 16,
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            <datalist id="dim-fields-list">
              {dimensionFields.map((f: string) => <option key={f} value={f} />)}
            </datalist>
            <button
              type="button"
              onClick={addDimension}
              style={{
                padding: '6px 14px',
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                color: theme.text,
              }}
            >
              + Add Dimension
            </button>
          </div>

          {/* Reporting Period */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Reporting Period
            </label>
            <input
              type="date"
              value={formData.reporting_period}
              onChange={(e) => setFormData({ ...formData, reporting_period: e.target.value })}
              required
              style={fieldInputStyle}
            />
          </div>

          {/* Type */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Type
            </label>
            <select
              value={formData.insight_type}
              onChange={(e) => setFormData({ ...formData, insight_type: e.target.value as 'Manual' | 'Generated' })}
              style={fieldInputStyle}
            >
              <option value="Manual">Manual</option>
              <option value="Generated">Generated</option>
            </select>
          </div>

          {/* Headline */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Headline
            </label>
            <input
              type="text"
              value={formData.headline}
              onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
              required
              style={fieldInputStyle}
            />
          </div>

          {/* Why (optional) */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Why
              <span style={{ fontWeight: 400, color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>
                Optional
              </span>
            </label>
            <textarea
              value={formData.why || ''}
              onChange={(e) => setFormData({ ...formData, why: e.target.value })}
              rows={2}
              style={{
                ...fieldInputStyle,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Action (optional) */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Action
              <span style={{ fontWeight: 400, color: theme.textSecondary, fontSize: 12, marginLeft: 8 }}>
                Optional
              </span>
            </label>
            <textarea
              value={formData.action || ''}
              onChange={(e) => setFormData({ ...formData, action: e.target.value })}
              rows={2}
              style={{
                ...fieldInputStyle,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Tags */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: theme.text }}>
              Tags
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add a tag..."
                style={{ ...fieldInputStyle, flex: 1 }}
              />
              <button
                type="button"
                onClick={addTag}
                style={{
                  padding: '8px 16px',
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  color: theme.text,
                }}
              >
                Add
              </button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {formData.tags.map((tag: string) => (
                  <span
                    key={tag}
                    style={{
                      padding: '4px 8px',
                      background: theme.background,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      color: theme.text,
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 14,
                        color: theme.textSecondary,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                cursor: 'pointer',
                color: theme.text,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                background: theme.buttonBackground,
                color: theme.buttonText,
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {insight ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
